import {
  buildDataset,
  buildFields,
  extractArray,
  isRecord,
  pickFirstNonEmpty,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import {
  SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES,
  SA_TIMESERIES_METRICS,
} from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/aggregated-metrics";
import {
  SA_GROUPS_TIMESERIES_COMPARE_ROWS_PROFILE,
  SA_GROUPS_TIMESERIES_ROWS_PROFILE,
} from "@/lib/ai/visualization/data/adapters/get-search-analytics-aggregated-data/profiles";
import {
  buildMetricFieldEntries,
  constrainMetricFieldsToFamilies,
  hasCoRealizedMetricFamilyPair,
  type MetricBlueprint,
  readFirstPathNumber,
  resolveMetricChartProfile,
  resolveRealizedDefaultMetricKey,
} from "@/lib/ai/visualization/data/adapters/metric-binding";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";
import { attachDerivedRankDependencies } from "./rank-dependencies";

const SA_GROUP_BY_VALUES = ["QUERY_GROUP", "URL_GROUP"] as const;

type SaGroupBy = (typeof SA_GROUP_BY_VALUES)[number];

const SA_GROUPED_DEFAULTS: NonNullable<VizDataset["defaults"]> = {
  x: "date",
  series: "group",
  category: "group",
  value: "sumOfClicks",
  y: ["sumOfClicks"],
};

function isSaGroupBy(value: unknown): value is SaGroupBy {
  return (
    typeof value === "string" &&
    (SA_GROUP_BY_VALUES as readonly string[]).includes(value)
  );
}

function resolveSaGroupByFromOutput(
  output: Record<string, unknown>,
): SaGroupBy | null {
  const context = isRecord(output.context) ? output.context : null;
  return isSaGroupBy(context?.groupBy) ? context.groupBy : null;
}

function inferSaGroupBy(items: unknown[]): SaGroupBy | null {
  if (items.some((item) => isRecord(item) && isRecord(item.queryGroup))) {
    return "QUERY_GROUP";
  }
  if (items.some((item) => isRecord(item) && isRecord(item.urlGroup))) {
    return "URL_GROUP";
  }
  return null;
}

function resolveSaGroupBy(args: {
  output: Record<string, unknown>;
  groupedItems: unknown[];
  comparingGroupedItems: unknown[];
}): SaGroupBy | null {
  const fromOutput = resolveSaGroupByFromOutput(args.output);
  if (fromOutput) return fromOutput;

  const candidates =
    args.groupedItems.length > 0
      ? args.groupedItems
      : args.comparingGroupedItems;
  return inferSaGroupBy(candidates);
}

function getSaGroupLabel(
  item: Record<string, unknown>,
  groupBy: SaGroupBy | null,
): string | null {
  const queryGroup = isRecord(item.queryGroup) ? item.queryGroup : null;
  const urlGroup = isRecord(item.urlGroup) ? item.urlGroup : null;
  const queryGroupLabel = queryGroup
    ? toString(queryGroup.name ?? queryGroup.id)
    : null;
  const urlGroupLabel = urlGroup
    ? toString(urlGroup.name ?? urlGroup.id)
    : null;

  if (groupBy === "QUERY_GROUP") return queryGroupLabel;
  if (groupBy === "URL_GROUP") return urlGroupLabel;
  return pickFirstNonEmpty(queryGroupLabel, urlGroupLabel);
}

function resolveGroupedTimeseriesDate(
  item: Record<string, unknown>,
): string | null {
  return (
    toString(
      item.date ?? item.day ?? item.week ?? item.month ?? item.dateKey,
    ) ?? "__all__"
  );
}

function readMetricValue(
  entry: Record<string, unknown>,
  metric: MetricBlueprint,
): number | null {
  const paths = metric.sourcePaths ?? [metric.sourceKey ?? metric.key];
  return readFirstPathNumber({ source: entry, paths }).value;
}

function buildGroupedTimeseriesRows(args: {
  items: unknown[];
  groupBy: SaGroupBy | null;
  metrics: MetricBlueprint[];
  period?: "current" | "previous";
}): VizDatasetRow[] {
  const rows: VizDatasetRow[] = [];

  for (const entry of args.items) {
    if (!isRecord(entry)) continue;
    const date = resolveGroupedTimeseriesDate(entry);
    const group = getSaGroupLabel(entry, args.groupBy);
    if (!date || !group) continue;

    const row: VizDatasetRow = {
      date,
      group,
      ...(args.period ? { period: args.period } : null),
    };

    let hasMetric = false;
    for (const metric of args.metrics) {
      const value = readMetricValue(entry, metric);
      row[metric.key] = value;
      hasMetric = hasMetric || typeof value === "number";
    }
    if (!hasMetric) continue;

    rows.push(row);
  }

  return rows.sort((left, right) => {
    const leftDate = toString(left.date) ?? "";
    const rightDate = toString(right.date) ?? "";
    if (leftDate !== rightDate) return leftDate.localeCompare(rightDate);

    const leftGroup = toString(left.group) ?? "";
    const rightGroup = toString(right.group) ?? "";
    if (leftGroup !== rightGroup) return leftGroup.localeCompare(rightGroup);

    const leftPeriod = toString(left.period) ?? "";
    const rightPeriod = toString(right.period) ?? "";
    return leftPeriod.localeCompare(rightPeriod);
  });
}

function buildSaGroupedRowsDataset(args: {
  source: VizDataset["source"];
  rows: VizDataset["rows"];
}): VizDataset {
  return buildDataset({
    id: "sa-agg-groups-timeseries-rows",
    profile: SA_GROUPS_TIMESERIES_ROWS_PROFILE,
    fields: buildFields([
      { key: "date", role: "x", type: "date" },
      { key: "group", role: "series", type: "string" },
      ...buildMetricFieldEntries(SA_TIMESERIES_METRICS),
    ]),
    rows: args.rows,
    defaults: SA_GROUPED_DEFAULTS,
    source: args.source,
  });
}

function buildSaGroupedCompareRowsDataset(args: {
  source: VizDataset["source"];
  rows: VizDataset["rows"];
  requestedMetricFamilies: ReadonlySet<string> | null;
}): VizDataset {
  const fields = constrainMetricFieldsToFamilies({
    fields: buildFields([
      { key: "date", role: "x", type: "date" },
      { key: "group", role: "series", type: "string" },
      { key: "period", role: "dim", type: "string" },
      ...buildMetricFieldEntries(SA_TIMESERIES_METRICS),
    ]),
    metricFamilies: args.requestedMetricFamilies,
  });
  const defaultMetricKey =
    resolveRealizedDefaultMetricKey({
      fields,
      rows: args.rows,
      preferredMetricFamily: "sumOfClicks",
    }) ?? "sumOfClicks";
  const supportsScatterPlot = hasCoRealizedMetricFamilyPair({
    rows: args.rows,
    fields,
  });

  return buildDataset({
    id: "sa-agg-groups-timeseries-compare-rows",
    profile: resolveMetricChartProfile({
      profile: SA_GROUPS_TIMESERIES_COMPARE_ROWS_PROFILE,
      rows: args.rows,
      fields,
      supportsScatterPlot,
    }),
    fields,
    rows: args.rows,
    defaults: {
      ...SA_GROUPED_DEFAULTS,
      value: defaultMetricKey,
      y: [defaultMetricKey],
    },
    source: args.source,
  });
}

/**
 * SA aggregated grouped rows を row-based dataset 群へ正規化する。
 *
 * @remarks
 * Search Analytics は groupBy 時でも `items` に long rows を返すため、
 * adapter で `date/group/metric` 形式へ寄せて時系列系・bar 系の双方で再利用する。
 * group ラベルが解決できない row は silent fallback せず除外する。
 */
export function buildSaGroupedDatasets(args: {
  output: Record<string, unknown>;
  source: VizDataset["source"];
  requestedMetricFamilies: ReadonlySet<string> | null;
}): VizDataset[] {
  const groupedItems = extractArray(args.output, "items");
  const comparingGroupedItems = extractArray(args.output, "comparingItems");
  const resolvedGroupBy = resolveSaGroupBy({
    output: args.output,
    groupedItems,
    comparingGroupedItems,
  });

  const datasets: VizDataset[] = [];
  const groupedRows = attachDerivedRankDependencies(
    buildGroupedTimeseriesRows({
      items: groupedItems,
      groupBy: resolvedGroupBy,
      metrics: SA_TIMESERIES_METRICS,
    }),
    SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES,
  );
  if (groupedRows.length > 0) {
    datasets.push(
      buildSaGroupedRowsDataset({
        source: args.source,
        rows: groupedRows,
      }),
    );
  }

  const groupedCurrentRows = attachDerivedRankDependencies(
    buildGroupedTimeseriesRows({
      items: groupedItems,
      groupBy: resolvedGroupBy,
      metrics: SA_TIMESERIES_METRICS,
      period: "current",
    }),
    SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES,
  );
  const groupedPreviousRows = attachDerivedRankDependencies(
    buildGroupedTimeseriesRows({
      items: comparingGroupedItems,
      groupBy: resolvedGroupBy,
      metrics: SA_TIMESERIES_METRICS,
      period: "previous",
    }),
    SA_TIMESERIES_DERIVED_RANK_DEPENDENCIES,
  );
  if (groupedCurrentRows.length > 0 && groupedPreviousRows.length > 0) {
    datasets.push(
      buildSaGroupedCompareRowsDataset({
        source: args.source,
        rows: [...groupedCurrentRows, ...groupedPreviousRows],
        requestedMetricFamilies: args.requestedMetricFamilies,
      }),
    );
  }

  return datasets;
}
